import React, { useState } from 'react';
import {
  Button,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Stack,
  Typography,
  InputAdornment,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { PaymentFormData, Payment } from '../../../types';
import { addPayment, getCase } from '../../../services/firebase';
import { formatCurrency } from '../../../utils/formatters';
import { toast } from 'react-toastify';

interface AddPaymentFormProps {
  caseId: string;
  /**
   * Initial outstanding balance to display before the case is re-fetched.
   * Pass this from the parent so the user never sees a $0.00 flash on open.
   */
  initialBalance?: number;
  onPaymentAdded: () => void;
  onCancel?: () => void;
}

const AddPaymentForm: React.FC<AddPaymentFormProps> = ({
  caseId,
  initialBalance = 0,
  onPaymentAdded,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentAmount, setCurrentAmount] = useState<number>(initialBalance);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<PaymentFormData>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      amount: 0,
    },
  });

  const watchAmount = watch('amount');

  React.useEffect(() => {
    let cancelled = false;
    const loadCaseDetails = async () => {
      try {
        const caseData = await getCase(caseId);
        if (cancelled || !caseData) return;
        const totalPaid = caseData.payments.reduce(
          (sum, p) => sum + (Number(p.amount) || 0),
          0,
        );
        setCurrentAmount(caseData.amountCharged - totalPaid);
      } catch (err) {
        console.error('Error loading case details:', err);
      }
    };
    loadCaseDetails();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const onSubmit = async (data: PaymentFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const caseData = await getCase(caseId);

      const hasPaymentOnDate = caseData.payments.some((payment) => payment.date === data.date);
      if (hasPaymentOnDate) {
        setError('A payment already exists for this date. Please choose a different date.');
        toast.error('A payment already exists for this date');
        setIsSubmitting(false);
        return;
      }

      const totalPaid = caseData.payments.reduce(
        (sum, payment) => sum + (Number(payment.amount) || 0),
        0,
      );
      const remaining = caseData.amountCharged - totalPaid;

      if (Number(data.amount) > remaining) {
        setError(`Payment amount exceeds remaining balance of ${formatCurrency(remaining)}`);
        toast.error('Payment amount exceeds remaining balance');
        setIsSubmitting(false);
        return;
      }

      const payment: Payment = {
        id: `payment_${Date.now()}`,
        amount: Number(data.amount),
        date: data.date,
        caseId,
      };

      await addPayment(caseId, payment);
      toast.success('Payment added successfully');
      reset();

      const updatedCaseData = await getCase(caseId);
      if (updatedCaseData) {
        const newTotalPaid = updatedCaseData.payments.reduce(
          (sum, p) => sum + (Number(p.amount) || 0),
          0,
        );
        setCurrentAmount(updatedCaseData.amountCharged - newTotalPaid);
      }

      onPaymentAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add payment');
      toast.error('Failed to add payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const enteredAmount = Number(watchAmount) || 0;
  const afterPayment = currentAmount - enteredAmount;

  const labelSx = {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 500,
    color: 'text.primary',
    mb: 1,
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: '#F9FAFB',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                Current balance
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatCurrency(currentAmount)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                After this payment
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: afterPayment <= 0 ? 'success.main' : 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatCurrency(afterPayment)}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography component="label" htmlFor="payment-amount" sx={labelSx}>
              Payment Amount
            </Typography>
            <TextField
              id="payment-amount"
              fullWidth
              type="number"
              placeholder="0.00"
              inputProps={{ step: '0.01', min: 0.01 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              {...register('amount', {
                required: 'Amount is required',
                min: { value: 0.01, message: 'Amount must be greater than 0' },
                max: {
                  value: currentAmount,
                  message: `Amount cannot exceed ${formatCurrency(currentAmount)}`,
                },
              })}
              error={!!errors.amount}
              helperText={errors.amount?.message}
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor="payment-date" sx={labelSx}>
              Payment Date
            </Typography>
            <TextField
              id="payment-date"
              {...register('date', { required: 'Date is required' })}
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.date}
              helperText={errors.date?.message}
              disabled={isSubmitting}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              pt: 1,
            }}
          >
            {onCancel && (
              <Button
                type="button"
                variant="text"
                onClick={onCancel}
                disabled={isSubmitting}
                sx={{ minHeight: 40 }}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ minHeight: 40, minWidth: 160 }}
              startIcon={
                isSubmitting ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
              }
            >
              {isSubmitting ? 'Processing…' : 'Process Payment'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
};

export default AddPaymentForm;
